// @redux-policy: transient-display-cache
import { createSlice } from '@reduxjs/toolkit';

export const initialState = {
  partners: [
    {
      info: {
        companyName: '',
        streetAddress: '',
        city: '',
        stateProvince: '',
        postalCode: '',
        country: '',
        contactPerson: '',
        email: '',
        phone: '',
        registrationNumber: '',
        annualTurnover: '',
        averageTenor: '60',
        creditLimit: '',
        currency: 'USD',
        financingRequested: '90',
        status: '',
        companyLogo: null, // Added this to hold the logo URL
      },
      documents: {
        latestFinancial: null,
        salesLedger: null,
        set1: {
          invoice: null,
          repaymentProof: null,
          purchaseOrder: null,
          billOfLading: null,
        },
        set2: {
          invoice: null,
          repaymentProof: null,
          purchaseOrder: null,
          billOfLading: null,
        },
      },
      invoices: [],
      termsheets: [],
      risks: [],
    },
  ],
  selectedTradePartnerId: null, // ID of the trade partner to focus on
};

const tradePartnerKYCSlice = createSlice({
  name: 'tradePartnerKYC',
  initialState,
  reducers: {
    resetTradePartnerKYC: () => {
      return { ...initialState };
    },
    setPartners: (state, action) => {
      state.partners = action.payload || [];
    },
    addTradePartner: (state) => {
      state.partners.push({ ...initialState.partners[0] });
    },
    removeTradePartner: (state, action) => {
      state.partners.splice(action.payload, 1);
    },
    updateTradePartnerInfo: (state, action) => {
      const { index, info } = action.payload;
      if (!state.partners[index]) {
        console.error(`Trade partner at index ${index} does not exist`);
        return;
      }
      if (!state.partners[index].info) {
        state.partners[index].info = { ...initialState.partners[0].info };
      }
      state.partners[index].info = {
        ...state.partners[index].info,
        ...info,
      };
    },
    updateTradePartnerDocument: (state, action) => {
      const { index, set, docType, file } = action.payload;
      if (set) {
        state.partners[index].documents[set][docType] = file;
      } else {
        state.partners[index].documents[docType] = file;
      }
    },
    removeTradePartnerDocument: (state, action) => {
      const { index, set, docType } = action.payload;
      if (set) {
        state.partners[index].documents[set][docType] = null;
      } else {
        state.partners[index].documents[docType] = null;
      }
    },
    setAddress: (state, action) => {
      const { index, address } = action.payload;
      state.partners[index].info.streetAddress = address.street;
      state.partners[index].info.city = address.city;
      state.partners[index].info.stateProvince = address.state;
      state.partners[index].info.postalCode = address.postalCode;
      state.partners[index].info.country = address.country;
    },
    setTradePartner: (state, action) => {
      state.partners[action.payload.index].info = {
        ...state.partners[action.payload.index].info,
        ...action.payload.info,
      };
    },
    addInvoice: (state, action) => {
      const { index, invoice } = action.payload;
      state.partners[index].invoices.push(invoice);
    },
    updateInvoice: (state, action) => {
      const { partnerIndex, invoiceIndex, invoice, ocrData, ocrFile } = action.payload;
      if (ocrData) {
        state.partners[partnerIndex].invoices[invoiceIndex] = {
          ...state.partners[partnerIndex].invoices[invoiceIndex],
          ...ocrData,
        };
      } else {
        state.partners[partnerIndex].invoices[invoiceIndex] = invoice;
      }

      // Sauvegarder le fichier OCR s'il est présent
      if (ocrFile) {
        state.partners[partnerIndex].invoices[invoiceIndex].ocrFile = ocrFile;
      }
    },
    removeInvoice: (state, action) => {
      const { partnerIndex, invoiceIndex } = action.payload;
      state.partners[partnerIndex].invoices.splice(invoiceIndex, 1);
    },
    addTermsheet: (state, action) => {
      const { index, termsheet } = action.payload;
      state.partners[index].termsheets.push(termsheet);
    },
    updateTermsheetElement: (state, action) => {
      const { partnerIndex, termsheetIndex, termsheet } = action.payload;
      state.partners[partnerIndex].termsheets[termsheetIndex] = {
        ...state.partners[partnerIndex].termsheets[termsheetIndex],
        ...termsheet,
      };
    },
    updateTermsheetStatus: (state, action) => {
      const { partnerIndex, termsheetIndex, status, reason, statusDate } = action.payload;
      state.partners[partnerIndex].termsheets[termsheetIndex].status = status;
      state.partners[partnerIndex].termsheets[termsheetIndex].reason = reason;
      state.partners[partnerIndex].termsheets[termsheetIndex].statusDate = statusDate;
    },
    removeTermsheet: (state, action) => {
      const { partnerIndex, termsheetIndex } = action.payload;
      state.partners[partnerIndex].termsheets.splice(termsheetIndex, 1);
    },
    addRisk: (state, action) => {
      const { index, risk } = action.payload;
      state.partners[index].risks.push(risk);
    },
    updateRiskElement: (state, action) => {
      const { entityIndex, riskIndex, risk } = action.payload;
      state.partners[entityIndex].risks[riskIndex] = risk;
    },
    removeRisk: (state, action) => {
      const { entityIndex, riskIndex } = action.payload;
      state.partners[entityIndex].risks.splice(riskIndex, 1);
    },
    updateRiskStatus: (state, action) => {
      const { entityIndex, riskIndex, status, reason, statusDate } = action.payload;
      const risk = state.partners[entityIndex].risks[riskIndex];
      risk.status = status;
      risk.reason = reason;
      risk.statusDate = statusDate;
    },
    updateCompanyLogo: (state, action) => {
      const { index, logo } = action.payload;
      state.partners[index].info.companyLogo = logo;
    },
    removeCompanyLogo: (state, action) => {
      const { index } = action.payload;
      state.partners[index].info.companyLogo = null;
    },
    setSelectedTradePartnerId: (state, action) => {
      state.selectedTradePartnerId = action.payload;
    },
  },
});

export const {
  setPartners,
  addTradePartner,
  removeTradePartner,
  updateTradePartnerInfo,
  updateTradePartnerDocument,
  removeTradePartnerDocument,
  setAddress,
  setTradePartner,
  addInvoice,
  updateInvoice,
  removeInvoice,
  addTermsheet,
  updateTermsheetElement,
  removeTermsheet,
  updateTermsheetStatus,
  addRisk,
  setSelectedTradePartnerId,
  updateRiskElement,
  removeRisk,
  updateRiskStatus,
  updateCompanyLogo,
  removeCompanyLogo,
} = tradePartnerKYCSlice.actions;

export const selectTradePartnerKYC = (state) => state.tradePartnerKYC;
export default tradePartnerKYCSlice.reducer;
